import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { userHasNotesAccess } from '../config/notesAccess';
import { listNotebooks, listSections, createPage, createNotebook, createSection } from '../oneNoteApi';
import { getNoteLinks, createNoteLink, deleteNoteLink } from '../api/figApi';

const INVALID_CHARS  = /[?*/\\:<>|'"]/;

function validateNotebookName(name) {
  if (!name.trim())           return 'Name is required.';
  if (name.length > 128)      return 'Max 128 characters.';
  if (INVALID_CHARS.test(name)) return 'Cannot contain: ? * / \\ : < > | \' "';
  return null;
}

function validateSectionName(name) {
  if (!name.trim())           return 'Name is required.';
  if (name.length > 50)       return 'Max 50 characters.';
  if (INVALID_CHARS.test(name)) return 'Cannot contain: ? * / \\ : < > | \' "';
  return null;
}

function formatLinkDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const S = {
  field: {
    display: 'block', width: '100%', fontSize: 14,
    padding: '8px 10px', boxSizing: 'border-box',
    border: '1px solid #ddd', borderRadius: 6, outline: 'none',
    background: '#fff',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  hint: { fontSize: 11, color: '#bbb', marginTop: 3 },
  fieldErr: { fontSize: 11, color: '#c0392b', marginTop: 3 },
  row: { marginBottom: 12 },
  newItemWrap: {
    marginTop: 6, padding: '8px 10px',
    background: '#fafafa', borderRadius: 6, border: '1px solid #eee',
  },
};

export default function LinkedNotes({ itemType, itemId, itemTitle, itemUrl }) {
  const { instance, accounts } = useMsal();
  const userUpn   = (accounts[0]?.username || '').toLowerCase();
  const isAllowed = userHasNotesAccess(accounts[0]);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  // ── existing notes list ────────────────────────────────────────────────────
  const [links,      setLinks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── form open/close ────────────────────────────────────────────────────────
  const [showForm,  setShowForm]  = useState(false);
  const [creating,  setCreating]  = useState(false);

  // ── notebook picker ────────────────────────────────────────────────────────
  const [notebooks,    setNotebooks]    = useState([]);
  const [loadingNbs,   setLoadingNbs]   = useState(false);
  const [nbError,      setNbError]      = useState(null);
  const [selectedNbId, setSelectedNbId] = useState('');
  const [showNewNb,    setShowNewNb]    = useState(false);
  const [newNbName,    setNewNbName]    = useState('');
  const [creatingNb,   setCreatingNb]   = useState(false);

  // ── section picker ─────────────────────────────────────────────────────────
  const [sections,      setSections]      = useState([]);
  const [loadingSecs,   setLoadingSecs]   = useState(false);
  const [secError,      setSecError]      = useState(null);
  const [selectedSecId, setSelectedSecId] = useState('');
  const [showNewSec,    setShowNewSec]    = useState(false);
  const [newSecName,    setNewSecName]    = useState('');
  const [creatingSec,   setCreatingSec]   = useState(false);

  // ── title ──────────────────────────────────────────────────────────────────
  const [formTitle, setFormTitle] = useState('');

  // ── data loaders ──────────────────────────────────────────────────────────
  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data  = await getNoteLinks(token, {
        sourceItemType: itemType,
        sourceItemId:   String(itemId),
        userUpn,
      });
      setLinks(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, itemType, itemId, userUpn]);

  const loadSections = useCallback(async (notebookId) => {
    setSections([]);
    setSelectedSecId('');
    setShowNewSec(false);
    setNewSecName('');
    setSecError(null);
    setLoadingSecs(true);
    try {
      const token = await getToken();
      const data  = await listSections(token, notebookId);
      const secs  = (data.value || []).map(s => ({ id: s.id, displayName: s.displayName }));
      setSections(secs);
      const lastId = localStorage.getItem(`defineholy.lastSectionId.${notebookId}.${userUpn}`);
      if (lastId && secs.find(s => s.id === lastId)) setSelectedSecId(lastId);
    } catch (err) {
      setSecError(err.message);
    } finally {
      setLoadingSecs(false);
    }
  }, [getToken, userUpn]);

  const loadNotebooks = useCallback(async () => {
    setLoadingNbs(true);
    setNbError(null);
    try {
      const token = await getToken();
      const data  = await listNotebooks(token);
      const nbs   = (data.value || []).map(nb => ({ id: nb.id, displayName: nb.displayName }));
      setNotebooks(nbs);
      const lastId = localStorage.getItem(`defineholy.lastNotebookId.${userUpn}`);
      if (lastId && nbs.find(nb => nb.id === lastId)) {
        setSelectedNbId(lastId);
        await loadSections(lastId);
      }
    } catch (err) {
      setNbError(err.message);
    } finally {
      setLoadingNbs(false);
    }
  }, [getToken, userUpn, loadSections]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAllowed) { setLoading(false); return; }
    loadLinks();
  }, [isAllowed, loadLinks]);

  useEffect(() => {
    if (!showForm) {
      // Reset all form state on close
      setSelectedNbId('');  setSelectedSecId('');
      setNotebooks([]);     setSections([]);
      setFormTitle('');
      setShowNewNb(false);  setShowNewSec(false);
      setNewNbName('');     setNewSecName('');
      setNbError(null);     setSecError(null);
      return;
    }
    loadNotebooks();
  }, [showForm, loadNotebooks]);

  // ── notebook handlers ──────────────────────────────────────────────────────
  async function handleNotebookSelect(value) {
    setSelectedNbId(value);
    setNbError(null);
    setShowNewNb(value === '__new__');
    setNewNbName('');
    if (value && value !== '__new__') await loadSections(value);
  }

  async function handleCreateNotebook() {
    const err = validateNotebookName(newNbName);
    if (err) { setNbError(err); return; }
    setCreatingNb(true);
    setNbError(null);
    try {
      const token = await getToken();
      const nb    = await createNotebook(token, newNbName.trim());
      setNotebooks(prev => [...prev, { id: nb.id, displayName: nb.displayName }]);
      setSelectedNbId(nb.id);
      setShowNewNb(false);
      setNewNbName('');
      await loadSections(nb.id);
    } catch (err) {
      setNbError(err.message);
    } finally {
      setCreatingNb(false);
    }
  }

  // ── section handlers ───────────────────────────────────────────────────────
  function handleSectionSelect(value) {
    setSelectedSecId(value);
    setSecError(null);
    setShowNewSec(value === '__new__');
    setNewSecName('');
  }

  async function handleCreateSection() {
    const err = validateSectionName(newSecName);
    if (err) { setSecError(err); return; }
    setCreatingSec(true);
    setSecError(null);
    try {
      const token = await getToken();
      const sec   = await createSection(token, selectedNbId, newSecName.trim());
      setSections(prev => [...prev, { id: sec.id, displayName: sec.displayName }]);
      setSelectedSecId(sec.id);
      setShowNewSec(false);
      setNewSecName('');
    } catch (err) {
      setSecError(err.message);
    } finally {
      setCreatingSec(false);
    }
  }

  // ── create note ────────────────────────────────────────────────────────────
  async function handleCreate() {
    if (!selectedNbId || selectedNbId === '__new__') {
      setNbError('Please select a notebook.'); return;
    }
    if (!selectedSecId || selectedSecId === '__new__') {
      setSecError('Please select a section.'); return;
    }
    const title = formTitle.trim();
    if (!title) return;

    setCreating(true);
    setError(null);
    try {
      const token = await getToken();

      const sourceLine = itemUrl
        ? `<p><i>Linked from DefineHoly: ${itemTitle || itemType} — <a href="${itemUrl}">View in app</a></i></p>`
        : `<p><i>Linked from DefineHoly: ${itemTitle || itemType}</i></p>`;
      const html = `<!DOCTYPE html><html><head><title>${title}</title></head><body>${sourceLine}<hr/></body></html>`;

      const page = await createPage(token, selectedSecId, html);

      await createNoteLink(token, {
        title,
        sourceItemType:   itemType,
        sourceItemId:     String(itemId),
        oneNotePageId:    page.id                            || '',
        oneNoteClientUrl: page.links?.oneNoteClientUrl?.href || '',
        oneNoteWebUrl:    page.links?.oneNoteWebUrl?.href    || '',
        userUpn,
      });

      localStorage.setItem(`defineholy.lastNotebookId.${userUpn}`, selectedNbId);
      localStorage.setItem(`defineholy.lastSectionId.${selectedNbId}.${userUpn}`, selectedSecId);

      await loadLinks();
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  // ── link list handlers ─────────────────────────────────────────────────────
  async function handleDeleteLink(linkId) {
    setDeletingId(linkId);
    setError(null);
    try {
      const token = await getToken();
      await deleteNoteLink(token, linkId);
      setLinks(prev => prev.filter(l => l.id !== linkId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  }

  function handleOpen(link) {
    const url = link.oneNoteClientUrl || link.oneNoteWebUrl;
    if (url) window.open(url, '_blank', 'noopener');
  }

  if (!isAllowed) return null;

  const canCreate = selectedNbId && selectedNbId !== '__new__' &&
                    selectedSecId && selectedSecId !== '__new__' &&
                    formTitle.trim();

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontSize: 14 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#444', letterSpacing: 0.2 }}>
        Notes
      </h4>

      {error && (
        <p style={{ color: '#c0392b', margin: '0 0 10px', fontSize: 13, lineHeight: 1.4 }}>{error}</p>
      )}

      {loading && (
        <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>Loading notes…</p>
      )}

      {!loading && links.length === 0 && !showForm && (
        <p style={{ color: '#bbb', fontSize: 13, margin: '0 0 10px' }}>
          No notes yet. Tap + New Note to add one.
        </p>
      )}

      {/* ── existing links ── */}
      {!loading && links.map(link => (
        <div
          key={link.id}
          style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', padding: '10px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
            <span style={{
              fontSize: 14, fontWeight: 500, display: 'block',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#222',
            }}>
              {link.title}
            </span>
            <span style={{ fontSize: 11, color: '#aaa', display: 'block', marginTop: 2 }}>
              {formatLinkDate(link.createdAt)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={() => handleOpen(link)}
              disabled={!!deletingId}
              style={{
                fontSize: 13, padding: '6px 12px', minHeight: 36,
                background: '#1a73e8', color: '#fff', border: 'none',
                borderRadius: 6, cursor: 'pointer', fontWeight: 500,
              }}
            >
              Open
            </button>
            <button
              onClick={() => handleDeleteLink(link.id)}
              disabled={deletingId === link.id}
              title="Remove link (OneNote page is NOT deleted)"
              aria-label="Remove note link"
              style={{
                fontSize: 17, color: '#ccc', background: 'none',
                border: 'none', cursor: 'pointer', padding: '6px 4px',
                minHeight: 36, lineHeight: 1,
              }}
            >
              {deletingId === link.id ? '…' : '×'}
            </button>
          </div>
        </div>
      ))}

      {/* ── create form ── */}
      {showForm ? (
        <div style={{ marginTop: 14 }}>

          {/* Notebook */}
          <div style={S.row}>
            <label style={S.label}>Notebook</label>
            {loadingNbs ? (
              <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>Loading…</p>
            ) : (
              <select
                value={selectedNbId}
                onChange={e => handleNotebookSelect(e.target.value)}
                style={S.field}
              >
                <option value="">— choose a notebook —</option>
                {notebooks.map(nb => (
                  <option key={nb.id} value={nb.id}>{nb.displayName}</option>
                ))}
                <option value="__new__">+ New notebook…</option>
              </select>
            )}
            {nbError && !showNewNb && <p style={S.fieldErr}>{nbError}</p>}

            {showNewNb && (
              <div style={S.newItemWrap}>
                <input
                  type="text"
                  value={newNbName}
                  onChange={e => setNewNbName(e.target.value)}
                  placeholder="Notebook name"
                  maxLength={128}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateNotebook(); }}
                  style={{ ...S.field, marginBottom: 4 }}
                />
                <p style={S.hint}>Max 128 chars · No: ? * / \ : &lt; &gt; | ' "</p>
                {nbError && <p style={S.fieldErr}>{nbError}</p>}
                <button
                  onClick={handleCreateNotebook}
                  disabled={creatingNb || !newNbName.trim()}
                  style={{
                    marginTop: 6, padding: '6px 14px', minHeight: 36,
                    background: '#1a73e8', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {creatingNb ? 'Creating…' : 'Create notebook'}
                </button>
              </div>
            )}
          </div>

          {/* Section */}
          <div style={S.row}>
            <label style={{ ...S.label, color: selectedNbId && selectedNbId !== '__new__' ? '#888' : '#ccc' }}>
              Section
            </label>
            {loadingSecs ? (
              <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>Loading…</p>
            ) : (
              <select
                value={selectedSecId}
                onChange={e => handleSectionSelect(e.target.value)}
                disabled={!selectedNbId || selectedNbId === '__new__'}
                style={{ ...S.field, color: !selectedNbId || selectedNbId === '__new__' ? '#bbb' : 'inherit' }}
              >
                <option value="">— choose a section —</option>
                {sections.map(s => (
                  <option key={s.id} value={s.id}>{s.displayName}</option>
                ))}
                <option value="__new__">+ New section…</option>
              </select>
            )}
            {secError && !showNewSec && <p style={S.fieldErr}>{secError}</p>}

            {showNewSec && (
              <div style={S.newItemWrap}>
                <input
                  type="text"
                  value={newSecName}
                  onChange={e => setNewSecName(e.target.value)}
                  placeholder="Section name"
                  maxLength={50}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateSection(); }}
                  style={{ ...S.field, marginBottom: 4 }}
                />
                <p style={S.hint}>Max 50 chars · No: ? * / \ : &lt; &gt; | ' "</p>
                {secError && <p style={S.fieldErr}>{secError}</p>}
                <button
                  onClick={handleCreateSection}
                  disabled={creatingSec || !newSecName.trim()}
                  style={{
                    marginTop: 6, padding: '6px 14px', minHeight: 36,
                    background: '#1a73e8', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {creatingSec ? 'Creating…' : 'Create section'}
                </button>
              </div>
            )}
          </div>

          {/* Title */}
          <div style={S.row}>
            <label style={S.label}>Title</label>
            <input
              type="text"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              placeholder="What's this note about?"
              onKeyDown={e => { if (e.key === 'Escape') setShowForm(false); }}
              style={S.field}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleCreate}
              disabled={creating || !canCreate}
              style={{
                flex: 1, padding: '10px', minHeight: 44,
                background: creating || !canCreate ? '#9ab8f0' : '#1a73e8',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 600,
                cursor: creating || !canCreate ? 'default' : 'pointer',
              }}
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              disabled={creating}
              style={{
                padding: '10px 20px', minHeight: 44,
                background: '#f5f5f5', color: '#555',
                border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          style={{
            marginTop: 10, padding: '8px 14px', minHeight: 40,
            background: 'none', border: '1px dashed #d0d0d0',
            borderRadius: 8, fontSize: 13, color: '#888',
            cursor: 'pointer', width: '100%', textAlign: 'left',
          }}
        >
          + New Note
        </button>
      )}
    </div>
  );
}
